import React, { useEffect, useState } from 'react';
import { useParams, NavLink, generatePath, useNavigate } from 'react-router-dom';
import { FlexColumn, FlexRow, HeaderText, SvgIcon, TableRow, TableRowType } from '@make-software/cspr-design';
import styled from 'styled-components';
import { getMarketplaceAppOffers } from 'app/console-api/marketplace/marketplace';
import { getPublicAppView } from 'app/console-api/apps/apps';
import { Offer } from 'app/console-api/offers/offers';
import { PaginationResponseType } from 'app/console-api';
import { Pagination, ErrorResult } from 'app/organizations-api/types';
import { StyledTableHeader } from 'app/components/common/layout/styled-elements';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import NoFound from 'assets/logos/no-found.svg';
import Table from 'app/components/common/table/table';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import { LayoutContainer, StyledTableFiltersBlock, TablePageContainer } from 'app/components/common/common-styles';
import { DealTypesFilter, DealTypesAdapter } from 'app/components/filters/deal-types-filter';
import { TaskTypeFilter, TaskTypeAdapter } from 'app/components/filters/task-type-filter';
import { FilterAdapter, useUrlFilterController } from 'app/components/filters/url-filters';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { MARKETPLACE_PATH, PUBLIC_APP_VIEW_PATH, SIGN_IN_PATH, JOIN_OFFER_PATH } from 'app/route/paths';
import { TradeType } from 'app/console-api/offers/offers';
import { MarketplaceAppOfferItem } from 'app/components/marketplace/marketplace-app-offer-item';

const BreadcrumbLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 16,
  color: theme.styleguideColors.fillPrimaryBlue,
  cursor: 'pointer'
}));

const BreadcrumbSeparator = styled.span(({ theme }) => ({
  color: theme.styleguideColors.fillPrimaryBlue,
  fontSize: 16
}));

const BreadcrumbCurrent = styled.span(({ theme }) => ({
  color: theme.styleguideColors.contentSecondary,
  fontSize: 16
}));

const ALL_ADAPTERS: FilterAdapter<any>[] = [DealTypesAdapter, TaskTypeAdapter];

export const MarketplaceAppOffersList = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [appName, setAppName] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 10 });
  const [offers, setOffers] = useState<PaginationResponseType<Offer> | null>(null);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  const ctl = useUrlFilterController();
  const filterCacheKey = ctl.buildCacheKey(ALL_ADAPTERS);

  useEffect(() => {
    if (!appId) return;
    getPublicAppView(appId)
      .then((app) => setAppName(app.name))
      .catch(() => {});
  }, [appId]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filterCacheKey]);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    getMarketplaceAppOffers(appId, pagination, {
      plugins: ctl.get(TaskTypeAdapter) ?? undefined,
      deal_types: (ctl.get(DealTypesAdapter) as TradeType[]) ?? undefined
    })
      .then((response) => {
        setOffers(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            default:
              setUnexpectedError(customErrorMessage);
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch marketplace app offers:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId, pagination, filterCacheKey]);

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize !== limit) {
      page = 1;
    }
    setPagination((prev) => ({ ...prev, currentPage: page, pageSize: limit }));
  };

  const handleJoin = ({ id }: Offer) => {
    navigate(generatePath(JOIN_OFFER_PATH, { id }));
  };

  const noOffers = !offers?.data?.length && !loading && !unexpectedError;

  return (
    <LayoutContainer>
      <FlexRow gap={8} align={'center'} style={{ marginBottom: '16px' }}>
        <BreadcrumbLink to={MARKETPLACE_PATH}>Marketplace</BreadcrumbLink>
        <BreadcrumbSeparator>›</BreadcrumbSeparator>
        {appId && appName ? (
          <>
            <BreadcrumbLink to={generatePath(PUBLIC_APP_VIEW_PATH, { appId })}>
              {appName}
            </BreadcrumbLink>
            <BreadcrumbSeparator>›</BreadcrumbSeparator>
          </>
        ) : null}
        <BreadcrumbCurrent>Offers</BreadcrumbCurrent>
      </FlexRow>
      <TablePageContainer>
        <FlexColumn>
          <StyledTableFiltersBlock>
            <DealTypesFilter />
            <TaskTypeFilter />
          </StyledTableFiltersBlock>
          {noOffers ? (
            <PreloadTableWindow
              icon={<SvgIcon src={NoFound} height={120} width={224} />}
              title="No offers yet"
            />
          ) : (
            <Table
              loading={loading}
              data={offers?.data || []}
              pageCount={offers?.page_count || 1}
              error={unexpectedError}
              pageSize={pagination.pageSize}
              currentPage={pagination.currentPage}
              handlePageActions={handlePageActions}
              tableRowType={TableRowType.TextSingleLine}
              renderDataHeader={() => (
                <TableRow>
                  <BaseTableHeader fixedWidthRem={25}>Offer</BaseTableHeader>
                  <BaseTableHeader fixedWidthRem={30}>Details</BaseTableHeader>
                  <BaseTableHeader fixedWidthRem={25}></BaseTableHeader>
                  <BaseTableHeader align="right">Action</BaseTableHeader>
                </TableRow>
              )}
              renderPaginatedData={(items) =>
                items.map((item) => (
                  <MarketplaceAppOfferItem key={item.id} offer={item} onJoin={handleJoin} />
                ))
              }
            />
          )}
        </FlexColumn>
      </TablePageContainer>
    </LayoutContainer>
  );
};
